import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _, { has, isFunction, merge } from 'lodash';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { Box } from '@material-ui/core';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { Controller, useFormContext } from 'react-hook-form';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import ValidationError from './ValidationError';
import { byteFormatter } from '../../utilities/FormattingUtilities';
import { useUniqueId } from '../../utilities/Hooks';
import NgpHiddenField from './NgpHiddenField';
import NgpFormButton from './NgpFormButton';

const useStyles = makeStyles((theme) => ({
    input: {
        display: 'none',
    },

    uploadErrorContainer: {
        marginBottom: theme.spacing(1),
    },

    helpText: {
        marginBottom: theme.spacing(1),
    },

    fileNameInput: {
        marginLeft: theme.spacing(3),
        marginTop: `0px !important`,
    },

    uploadButton: {
        ...theme.mixins.flex.justifyContentFlexStart,

        width: '100%',

        '&$shrink': {
            width: 'auto',
        },
    },

    dragging: {
        backgroundColor: theme.palette.secondary.dark,
    },

    validatedField: {
        // '&$valid': { color: theme.palette.success.dark },
        '&$error': { color: theme.palette.error.main },
    },

    shrink: {},
    // valid: {},
    error: {},
}));

export default function NgpFileUpload(props) {
    const {
        fieldName,
        fileNameFieldName,
        label,
        icon,
        maxFileSize,
        defaultValue,
        required,
        requiredFileType,

        helpText,
        accept,
        formSubmit, // Indicates this is a traditional form submit, rather than mapping the value to a field
        showFileName,
        showFileSize,

        buttonProps,

        container: Container,
        containerProps,

        rules: rulesProp,
        convertValue,

        shrink,
    } = props;

    const classes = useStyles();
    const { control, setError, errors, setValue, getValues, clearErrors } = useFormContext();
    const uploadFieldId = useUniqueId('ngp-file-upload-');

    const [fileState, setFileState] = useState();

    // Reference counter in order to counteract issues with dragleave and dragenter firing when hovering a child element
    // see: https://stackoverflow.com/questions/7110353/html5-dragleave-fired-when-hovering-a-child-element
    const [dragCount, setDragCount] = useState(0);
    const onDragEnter = () => setDragCount((n) => n + 1);
    const onDragLeave = () => setDragCount((n) => n - 1);
    const isDragover = dragCount !== 0;

    const handleFileInputChange = (onChange) => async (event) => {
        const [file] = event?.target?.files || event?.dataTransfer?.files || [];
        if (!file) return;

        // validate file type
        if (!_.isEmpty(requiredFileType) && file.name.split('.').pop() !== requiredFileType) {
            setError(fieldName, {
                message: i18n._(t`File is not of the required type: ${requiredFileType}`),
                type: 'fileType',
            });
            return;
        }

        clearErrors(fieldName);

        const { name, size } = file;

        let value = file;
        if (formSubmit !== true) {
            value = await readFileData(file);
        }

        if (isFunction(convertValue)) {
            try {
                value = convertValue(value);
            } catch (e) {
                setError(fieldName, { message: String(e), type: 'convertValue' });
            }
        }

        const meta = { name, size, value };

        setFileState(meta);
        onChange(value); // onChange comes from the form controller
        setDragCount(0);
    };

    const isInvalid = has(errors, fieldName);
    const buttonLabel = getUploadButtonLabel({
        showFileName,
        showFileSize,
        label,
        fileState,
    });

    const fileName = fileState?.name;
    useEffect(() => {
        // sync the filename field if it is needed
        if (fileNameFieldName && fileName) {
            const currFileName = getValues(fileNameFieldName);

            if (currFileName !== fileName) {
                setValue(fileNameFieldName, fileName);
            }
        }
    }, [fileName, fileNameFieldName, getValues, setValue]);

    const rules = merge(
        {
            required: required === true ? 'A file is required' : false,
            validate: {
                size: (value) => {
                    const size = fileState?.size;

                    if (fileState && value && size && size > maxFileSize) {
                        return i18n._(t`File is larger than ${byteFormatter(maxFileSize)} maximum`);
                    }

                    return true;
                },
            },
        },
        // only apply external rules if we actually have uploaded a file
        fileState && rulesProp
    );

    return (
        <>
            <Controller
                control={control}
                name={fieldName}
                defaultValue={defaultValue}
                rules={rules}
                render={({ onChange }) => (
                    <Container {...(containerProps || {})}>
                        <input
                            type="file"
                            accept={accept}
                            className={classes.input}
                            id={uploadFieldId}
                            onChange={handleFileInputChange(onChange)}
                        />

                        <label htmlFor={uploadFieldId}>
                            <div
                                className={clsx(classes.validatedField, {
                                    [classes.error]: isInvalid,
                                })}
                            >
                                <NgpFormButton
                                    className={clsx(
                                        classes.uploadButton,
                                        isDragover && classes.dragging,
                                        shrink && classes.shrink
                                    )}
                                    aria-label={<Trans>Upload file</Trans>}
                                    component="span"
                                    startIcon={icon}
                                    {...(buttonProps || {})}
                                    {...(fileState && isInvalid ? { color: 'inherit' } : {})}
                                    onDrop={handleFileInputChange(onChange)}
                                    onDragLeave={onDragLeave}
                                    onDragEnter={onDragEnter}
                                >
                                    {buttonLabel}
                                </NgpFormButton>
                            </div>
                        </label>

                        {helpText && <div className={classes.helpText}>{helpText}</div>}

                        <div className={classes.uploadErrorContainer}>
                            <ValidationError
                                invalid={_.get(errors, fieldName, false)}
                                message={_.get(errors, `${fieldName}.message`)}
                            />
                        </div>
                    </Container>
                )}
            />
            {fileNameFieldName && <NgpHiddenField fieldName={fileNameFieldName} />}
        </>
    );
}

NgpFileUpload.propTypes = {
    fieldName: PropTypes.string.isRequired,
    fileNameFieldName: PropTypes.string,
    formSubmit: PropTypes.bool,
    showFileName: PropTypes.bool,
    showFileSize: PropTypes.bool,
    defaultValue: PropTypes.any,
    shrink: PropTypes.bool,

    convertValue: PropTypes.func,
    rules: PropTypes.object,

    maxFileSize: PropTypes.number,
    label: PropTypes.any,
    icon: PropTypes.node,

    buttonProps: PropTypes.object,
    container: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
    containerProps: PropTypes.object,
};

NgpFileUpload.defaultProps = {
    formSubmit: false,
    fileNameFieldName: null,
    showFileName: true,
    showFileSize: true,
    defaultValue: null,
    shrink: false,

    convertValue: null,
    rules: {},

    maxFileSize: 2.202e7, // 2.202e+7 bytes ≈ 20 MB,
    label: t`Upload`,
    icon: <CloudUploadIcon />,

    buttonProps: null,
    container: Box,
    containerProps: null,
};

function getUploadButtonLabel({ showFileName, showFileSize, fileState, label }) {
    if (!fileState || showFileName !== true) return label;

    const { name, size } = fileState;
    let btnLabel = name;

    if (showFileSize) {
        btnLabel += ` (${byteFormatter(size)})`;
    }

    return btnLabel;
}

async function readFileData(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.addEventListener('load', (e) => resolve(e?.target?.result));
        reader.addEventListener('error', reject);
        reader.readAsText(file);
    });
}
